import { CommonModule } from '@angular/common';
import { Component, Input, AfterViewInit, ViewChild, OnInit, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTabChangeEvent, MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { TranslateModule } from '@ngx-translate/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { ITab, ITabChildren } from 'src/app/shared/interfaces/ITab';
import { DragTabsDirective } from './drag-tabs.directive';

@Component({
  selector: 'app-tab',
  standalone: true,
  templateUrl: './tab.component.html',
  styleUrl: './tab.component.scss',
  imports: [
    MatTabsModule,
    MatCardModule,
    MatIconModule,
    TranslateModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    DragTabsDirective
  ]
})
export class AppTabComponent implements AfterViewInit, OnInit {
  @Input() tabs: ITab;
  @Input() selectTabIndex = 0;
  @Input() class = '';
  @Output() tabChanged = new EventEmitter<ITabChildren>();
  @ViewChild(MatTabGroup) tabGroup: MatTabGroup;
  container: HTMLElement;
  tabContents: ITabChildren[] = [];
  isLoading = false;
  routerOutlet = false;

  disabledSubject = new Subject<any>();
  disabledObservable$ = this.disabledSubject.asObservable();

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    this.disabledObservable$.subscribe((value) => {
      const tab = this.tabContents.find((item) => item.id && value?.id && item.id == value?.id);
      if (tab) {
        tab.disabled = value?.disabled ?? false;
      }
    });
    await this.loadTabsDataFromApi();
    this.updateTabFromUrl();
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.updateTabFromUrl();
      }
    });
    this.changeTabContainerStyle();
  }

  ngAfterViewInit() {
    this.container = this.tabGroup._elementRef.nativeElement;
    this.changeTabContainerStyle();
    this.changeActiveTabStyle();

    if (typeof this.tabs?.selectTabIndex == 'function') {
      if (this.tabs.selectTabIndex() > -1) {
        this.selectTabIndex = this.tabs.selectTabIndex();
      }
    } else if ((this.tabs?.selectTabIndex ?? -1) > -1) {
      this.selectTabIndex = this.tabs.selectTabIndex ?? 0;
    }

    this.cdr.detectChanges();
  }

  async loadTabsDataFromApi() {
    if (typeof this.tabs?.children === 'function') {
      if (this.tabs.showLoading) {
        this.isLoading = true;
      }
      this.tabContents = await this.tabs.children();
    } else {
      this.tabContents = this.tabs?.children ?? [];
    }
    this.isLoading = false;

    this.tabContents.forEach(async (item) => {
      if (typeof item.disabled === 'function') {
        const disabled = await item.disabled(this.disabledSubject);
        item.disabled = disabled;
      } else {
        item.disabled = item.disabled ?? false;
      }

      if (typeof item.hidden === 'function') {
        const hidden = await item.hidden();
        item.hidden = hidden;
      } else {
        item.hidden = item.hidden ?? false;
      }
    });

    this.cdr.detectChanges();
  }

  updateTabFromUrl() {
    if (this.router.url) {
      this.tabContents.forEach((value, index): any => {
        let urls = this.router.url.split('/').filter(Boolean);
        const paths = this.getTabPath(value).split('/').filter(Boolean);
        const newPath = paths.map((value, index): any => {
          if (value[0] == ':') {
            value = urls[index];
          }
          return value;
        });
        if (urls.length > newPath.length) {
          urls = urls.slice(0, newPath.length);
        }
        if (paths.length > 0 && urls.join('/') == newPath.join('/')) {
          this.selectTabIndex = index;
          this.routerOutlet = false;
        }
        if (paths.length > 0 && !this.contentHasChildIncludesRoute()) {
          this.routerOutlet = true;
        }
      });
    }
  }

  contentHasChildIncludesRoute() {
    // check content has child includes current url
    // if has child, use recursive without use routerOutlet
    return this.tabContents.some((item) => {
      if (item.children) {
        let urls = this.router.url.split('/').filter(Boolean);
        const paths = this.getTabPath(item).split('/').filter(Boolean);
        const newPath = paths.map((value, index): any => {
          if (value[0] == ':') {
            value = urls[index];
          }
          return value;
        });
        if (urls.length > newPath.length) {
          urls = urls.slice(0, newPath.length);
        }
        return paths.length > 0 && urls.join('/') == newPath.join('/');
      }
      return false;
    });
  }

  changeTabContainerStyle() {
    const tabContainer = this.container.querySelector('.mat-mdc-tab-header') as HTMLElement;
    if (tabContainer) {
      tabContainer.style.backgroundColor = this.tabs?.tabStyles?.containerBackground ?? '';
      const borderColor = this.tabs?.tabStyles?.containerBorderColor ?? 'transparent';
      const borderWidth = `${this.tabs?.tabStyles?.containerBorderWidth ?? 0}px`;
      tabContainer.style.border = `${borderWidth} solid ${borderColor}`;
      Object.assign(tabContainer.style, this.tabs?.tabStyles?.tabHeaderStyle);
    }

    const tabItemFirst = tabContainer.querySelector('.mdc-tab.mat-mdc-tab:first-child') as HTMLElement;
    if (tabItemFirst) {
      tabItemFirst.style.borderTopLeftRadius = `${this.tabs?.tabStyles?.containerBorderRadius ?? 0}px`;
      tabItemFirst.style.borderBottomLeftRadius = `${this.tabs?.tabStyles?.containerBorderRadius ?? 0}px`;
      const tabItemLast = tabContainer.querySelector('.mdc-tab.mat-mdc-tab:last-child') as HTMLElement;
      tabItemLast.style.borderTopRightRadius = `${this.tabs?.tabStyles?.containerBorderRadius ?? 0}px`;
      tabItemLast.style.borderBottomRightRadius = `${this.tabs?.tabStyles?.containerBorderRadius ?? 0}px`;

      const tabItem = tabContainer.querySelectorAll('.mdc-tab.mat-mdc-tab') as NodeListOf<HTMLElement>;
      tabItem.forEach((item) => Object.assign(item.style, this.tabs?.tabStyles?.tabItemStyle));
    }
  }

  onTabChange(event: MatTabChangeEvent) {
    const selectedTabContent = this.tabContents[event.index];
    selectedTabContent.selectTabIndex = event.index;
    this.tabChanged.emit(selectedTabContent);
    this.removePreviousActiveTabClass();
    this.changeActiveTabStyle();
    this.selectTabIndex = event.index;
    this.cdr.detectChanges();
  }

  onTabClick(tab: ITabChildren) {
    this.updateUrl(tab);
  }

  updateUrl(tab: ITabChildren) {
    const urls = this.router.url.split('/').filter(Boolean);
    const paths = this.getTabPath(tab).split('/').filter(Boolean);
    if ((paths[0] && urls[0] == paths[0]) || (paths.length == 1 && urls.length == 1)) {
      paths.forEach((item, index) => {
        if (item[0] == ':') {
          paths[index] = urls[index];
        }
      });
    }
    if (paths.length > 0) {
      this.router.navigateByUrl(paths.filter(Boolean).join('/'));
    }
  }

  changeActiveTabStyle(): void {
    // const activeTab = this.container.getElementsByClassName('mdc-tab--active')[0] as HTMLElement;
    // if (activeTab) {
    //   activeTab.style.backgroundColor = this.tabs?.tabStyles?.activeTabBackground ?? '';
    //   activeTab.classList.add('active-tab');
    // }
    // if (this.tabs?.tabStyles?.activeTabUnderlineColor && activeTab) {
    //   const underline = activeTab.querySelector('.mdc-tab-indicator__content--underline') as HTMLElement;
    //   if (underline) {
    //     underline.style.borderColor = this.tabs?.tabStyles?.activeTabUnderlineColor;
    //   }
    // }

    // const tabDeActive = this.container.querySelectorAll('.mdc-tab.mat-mdc-tab:not(.active-tab)') as NodeListOf<HTMLElement>;
    // if (tabDeActive) {
    //   tabDeActive.forEach((item) => Object.assign(item.style, this.tabs?.tabStyles?.deActiveItemStyle));
    // }
  }

  removePreviousActiveTabClass(): void {
    const previousActiveTab = this.container.getElementsByClassName('active-tab')[0] as HTMLElement;
    if (previousActiveTab) {
      previousActiveTab.style.backgroundColor = 'unset';
      previousActiveTab?.classList.remove('active-tab');
    }
  }

  getTextStyle(active: boolean) {
    if (active) {
      return {
        ...this.tabs?.tabStyles?.textStyle,
        ...this.tabs?.tabStyles?.activeTextStyle
      };
    }
    return { ...this.tabs?.tabStyles?.textStyle };
  }

  getBadge(tab: ITabChildren): string | number | undefined {
    return typeof tab.badge === 'function' ? tab.badge() : tab.badge;
  }

  getChildrenTab = (tab: ITab) => (typeof tab?.children == 'function' ? tab?.children() : tab?.children) ?? [];
  getTabPath = (tab: ITabChildren) => (typeof tab?.path == 'function' ? tab?.path() : tab?.path) ?? '';
}
